import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { isCodedError } from '~/common/errors/CodedError';
import { Breadcrumbs } from '~/eds/src/components/Breadcrumbs/Breadcrumbs';
import { FullPageOverlay } from '~/eds/src/components/FullPageOverlay/FullPageOverlay';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { UserAction } from '~/eds/src/types/UserAction';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import {
  Playbook,
  PlaybookWithId,
} from '~/redux/api/types/playbooks/playbooks';

import { FirstStep, Option } from './first-step/FirstStep';
import { Form } from './form/Form';
import { TemplateStep } from './template-step/TemplateStep';
import { SetupStep, useSetupWizard } from './useSetupWizard';
import { getPlaybookErrors, isPlaybookDirty, isPlaybookValid } from './util';

const EMPTY_PLAYBOOK = (): Playbook => ({
  name: '',
  description: '',
  restrictions: {
    workflow_ids: [] as string[],
    contract_types: [] as string[],
  },
  guidelines: [
    {
      id: uuidv4(),
      name: '',
      sample: '',
      guideline: '',
      suggest_redlines: false,
    },
  ],
});

type Props = {
  playbook?: PlaybookWithId;
  onClose: () => void;
};

export const Setup = ({ playbook: playbookToEdit, onClose }: Props) => {
  const intl = useIntl();
  const isDraftPublishEnabled = useFlag(FlagType.PlaybookDraftPublish);
  const { toast } = useToast();
  const wizard = useSetupWizard(
    playbookToEdit ? SetupStep.Form : SetupStep.FirstStep,
  );

  const [playbook, setPlaybook] = useState<Playbook>(() =>
    playbookToEdit
      ? {
          ...playbookToEdit,
          guidelines: playbookToEdit.guidelines?.map((guideline) => ({
            ...guideline,
            id: guideline.uuid,
            isReadOnly: true,
          })),
        }
      : EMPTY_PLAYBOOK(),
  );

  const [createPlaybook, { isLoading: isCreatingPlaybook }] =
    api.endpoints.createPlaybook.useMutation();
  const [updatePlaybook, { isLoading: isUpdatingPlaybook }] =
    api.endpoints.updatePlaybook.useMutation();
  const [getTemplate, { isLoading: isLoadingTemplate }] =
    api.endpoints.getPlaybookTemplate.useLazyQuery();

  const errors = useMemo(() => getPlaybookErrors(playbook), [playbook]);

  const handleOptionSelect = (option: Option) => {
    if (option !== Option.Template) {
      setPlaybook(EMPTY_PLAYBOOK());
      wizard.setSelectedTemplateId(null);
    }
    wizard.handleOptionSelect(option);
  };

  const handleContinueFromTemplate = async () => {
    if (!wizard.selectedTemplateId) return;
    const result = await getTemplate({
      templateId: wizard.selectedTemplateId,
    });
    if (result.data) {
      const template = result.data.data.attributes;
      setPlaybook({
        name: template.name,
        description: template.description,
        restrictions: {
          workflow_ids: [] as string[],
          contract_types: [] as string[],
        },
        guidelines: template.guidelines.map((g) => ({
          id: uuidv4(),
          name: g.name,
          guideline: g.guideline,
          sample: g.sample,
          suggest_redlines: g.suggest_redlines,
        })),
      });
      wizard.navigateToForm();
    } else {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'playbooks.Setup.loadTemplateErrorToast',
          defaultMessage:
            'Failed to load the selected template. Please try again.',
          description: 'Error toast when fetching a playbook template fails',
        }),
      });
    }
  };

  const handleSubmit = async () => {
    try {
      if (playbookToEdit) {
        await updatePlaybook({
          playbookId: playbookToEdit.uuid,
          updatePlaybookRequest: {
            data: {
              type: 'playbooks',
              attributes: isDraftPublishEnabled
                ? { ...playbook, status: playbookToEdit.status }
                : playbook,
            },
          },
        }).unwrap();
      } else {
        await createPlaybook({
          data: {
            type: 'playbooks',
            attributes: isDraftPublishEnabled
              ? { ...playbook, status: 'DRAFT' }
              : playbook,
          },
        }).unwrap();
      }
      onClose();
    } catch (err) {
      if (isCodedError(err)) {
        return toast({
          status: 'danger',
          message: err.message,
        });
      }
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'playbooks.Setup.createPlaybookErrorToast',
          defaultMessage:
            'An error occurred while submitting the playbook. Please try again.',
          description: 'Error toast message shown when playbook creation fails',
        }),
      });
    }
  };

  const isDraftPlaybook =
    isDraftPublishEnabled && playbookToEdit?.status === 'DRAFT';
  const isPublishedPlaybook =
    isDraftPublishEnabled && playbookToEdit?.status === 'PUBLISHED';

  const [unpublishModal, showUnpublishModal, hideUnpublishModal] = useModal({
    title: intl.formatMessage({
      id: 'playbooks.Setup.unpublishConfirmTitle',
      defaultMessage: 'Unpublish Playbook?',
      description: 'Title for unpublish playbook confirmation modal',
    }),
    primaryAction: {
      text: intl.formatMessage({
        id: 'playbooks.Setup.unpublishConfirmAction',
        defaultMessage: 'Unpublish',
        description:
          'Label for primary action in unpublish playbook confirmation modal',
      }),
      onClick: async () => {
        if (!playbookToEdit) return;
        try {
          const { uuid: _uuid, ...playbookAttributes } = playbookToEdit;
          await updatePlaybook({
            playbookId: playbookToEdit.uuid,
            updatePlaybookRequest: {
              data: {
                type: 'playbooks',
                attributes: {
                  ...playbookAttributes,
                  guidelines: playbookAttributes.guidelines?.map((g) => ({
                    ...g,
                    id: g.uuid,
                  })),
                  status: 'DRAFT',
                },
              },
            },
          }).unwrap();
          hideUnpublishModal();
          onClose();
        } catch (err) {
          if (isCodedError(err)) {
            return toast({
              status: 'danger',
              message: err.message,
            });
          }
          toast({
            status: 'danger',
            message: intl.formatMessage({
              id: 'playbooks.Setup.createPlaybookErrorToast',
              defaultMessage:
                'An error occurred while submitting the playbook. Please try again.',
              description:
                'Error toast message shown when playbook creation fails',
            }),
          });
        }
      },
      isLoading: isUpdatingPlaybook,
      variant: 'danger',
    },
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          {intl.formatMessage({
            id: 'playbooks.Setup.unpublishConfirmMessage',
            defaultMessage:
              'Unpublishing this playbook will remove it from active AI reviews. Users will no longer be able to use this playbook for AI review until it is published again. Do you want to continue?',
            description:
              'Warning message in unpublish playbook confirmation modal',
          })}
        </Text>
      </Layout>
    ),
  });

  const publishAction = isDraftPlaybook
    ? {
        text: intl.formatMessage({
          id: 'playbooks.Setup.publishButtonLabel',
          defaultMessage: 'Publish',
          description:
            'Label for the publish button when editing a draft playbook',
        }),
        disabled: !isPlaybookValid(playbook),
        tooltip: errors.length > 0 ? errors.join('\n') : undefined,
        isLoading: isUpdatingPlaybook,
        onClick: async () => {
          try {
            const { uuid: _uuid, ...playbookAttributes } = playbookToEdit;
            await updatePlaybook({
              playbookId: playbookToEdit.uuid,
              updatePlaybookRequest: {
                data: {
                  type: 'playbooks',
                  attributes: {
                    ...playbookAttributes,
                    guidelines: playbookAttributes.guidelines?.map((g) => ({
                      ...g,
                      id: g.uuid,
                    })),
                    status: 'PUBLISHED',
                  },
                },
              },
            }).unwrap();
            onClose();
          } catch (err) {
            if (isCodedError(err)) {
              return toast({
                status: 'danger',
                message: err.message,
              });
            }
            toast({
              status: 'danger',
              message: intl.formatMessage({
                id: 'playbooks.Setup.createPlaybookErrorToast',
                defaultMessage:
                  'An error occurred while submitting the playbook. Please try again.',
                description:
                  'Error toast message shown when playbook creation fails',
              }),
            });
          }
        },
        level: 'secondary' as const,
      }
    : null;

  const unpublishAction = isPublishedPlaybook
    ? {
        text: intl.formatMessage({
          id: 'playbooks.Setup.unpublishButtonLabel',
          defaultMessage: 'Unpublish',
          description:
            'Label for the unpublish button when editing a published playbook',
        }),
        disabled: !isPlaybookValid(playbook),
        tooltip: errors.length > 0 ? errors.join('\n') : undefined,
        onClick: showUnpublishModal,
        level: 'secondary' as const,
      }
    : null;

  const footerActions = useMemo((): UserAction[] => {
    switch (wizard.step) {
      case SetupStep.Template:
        return [
          {
            text: intl.formatMessage({
              id: 'playbooks.Setup.continueButtonLabel',
              defaultMessage: 'Continue',
              description: 'Label for the continue button in the template step',
            }),
            disabled: !wizard.selectedTemplateId,
            isLoading: isLoadingTemplate,
            onClick: handleContinueFromTemplate,
            level: 'primary' as const,
          },
        ];

      case SetupStep.Form: {
        const isSubmitDisabled =
          !isPlaybookValid(playbook) ||
          (playbookToEdit && !isPlaybookDirty(playbook, playbookToEdit));

        return [
          ...(isDraftPublishEnabled && publishAction ? [publishAction] : []),
          ...(isDraftPublishEnabled && unpublishAction
            ? [unpublishAction]
            : []),
          {
            text: playbookToEdit
              ? intl.formatMessage({
                  id: 'playbooks.Setup.doneButtonLabel',
                  defaultMessage: 'Submit',
                  description:
                    'Label for the done button in the setup playbook modal',
                })
              : intl.formatMessage({
                  id: 'playbooks.Setup.createButtonLabel',
                  defaultMessage: 'Create',
                  description:
                    'Label for the create button in the setup playbook modal',
                }),
            tooltip: errors.length > 0 ? errors.join('\n') : undefined,
            isLoading: isCreatingPlaybook || isUpdatingPlaybook,
            disabled: isSubmitDisabled,
            onClick: handleSubmit,
            level: 'primary' as const,
          },
        ];
      }

      default:
        return [];
    }
  }, [
    wizard.step,
    wizard.selectedTemplateId,
    isLoadingTemplate,
    isDraftPublishEnabled,
    publishAction,
    unpublishAction,
    playbook,
    playbookToEdit,
    errors,
    isCreatingPlaybook,
    isUpdatingPlaybook,
    intl,
  ]);

  const renderStep = () => {
    switch (wizard.step) {
      case SetupStep.FirstStep:
        return <FirstStep onSelect={handleOptionSelect} />;
      case SetupStep.Template:
        return (
          <TemplateStep
            selectedTemplateId={wizard.selectedTemplateId}
            onSelectTemplate={wizard.setSelectedTemplateId}
          />
        );
      case SetupStep.Form:
        return <Form value={playbook} onChange={setPlaybook} />;
      default:
        return null;
    }
  };

  return (
    <>
      {unpublishModal}
      <FullPageOverlay
        isVisible
        isFullPage
        backgroundGradient="library"
        onHide={onClose}
        title={
          playbookToEdit?.uuid
            ? intl.formatMessage({
                id: 'playbooks.Setup.editPlaybookTitle',
                defaultMessage: 'Edit Playbook',
                description: 'Title for the edit playbook modal',
              })
            : intl.formatMessage({
                id: 'playbooks.Setup.createPlaybookTitle',
                defaultMessage: 'Create Playbook',
                description: 'Title for the create playbook modal',
              })
        }
        actions={footerActions}
        cancelAction={{
          text: intl.formatMessage({
            id: 'playbooks.Setup.closeButtonLabel',
            defaultMessage: 'Cancel',
            description:
              'Label for the close button in the setup playbook modal',
          }),
          onClick: onClose,
          level: 'tertiary',
        }}
      >
        <Layout direction="column" flex="1">
          {wizard.breadcrumbs && (
            <Layout mb={4}>
              <Breadcrumbs actions={wizard.breadcrumbs} />
            </Layout>
          )}
          {renderStep()}
        </Layout>
      </FullPageOverlay>
    </>
  );
};
